/**
 * Rhode Island EMS billing catalog — addendum § 4.
 *
 * Source of truth for the HCPCS codes, origin-destination modifiers,
 * level-of-service validation, and denial-prevention checks EMSPCR
 * runs against every finalized transport ePCR.
 *
 * The catalog is intentionally declarative — additional states can
 * layer their own HCPCS/modifier sets on top via the Admin Console
 * (addendum § 6) without changing code.
 */

import type { EPCR, Procedure, MedicationGiven } from '@/types';

// ============================================================
// HCPCS base transport codes — public-sector EMS (addendum § 4.1)
// ============================================================
export interface HcpcsCode {
  code: string;
  label: string;
  payerCoverage: string;
  /** What documentation triggers auto-selection of this code. */
  trigger: string;
  /** 'base' | 'mileage' | 'supply' — billing pack category */
  category: 'base' | 'mileage' | 'supply' | 'no_transport';
}

export const HCPCS_CODES: HcpcsCode[] = [
  { code: 'A0427', label: 'ALS Emergency Transport, Level 1', payerCoverage: 'Medicare / Medicaid / Commercial',
    trigger: 'Emergency dispatch + ALS interventions (IV, ALS meds, 12-lead, advanced airway, cardiac monitoring)',
    category: 'base' },
  { code: 'A0433', label: 'ALS Emergency Transport, Level 2 (ALS-2)', payerCoverage: 'Medicare / Medicaid / Commercial',
    trigger: '3+ ALS medications OR one of: manual defib, TCP, chest decompression, cardioversion, ETT intubation',
    category: 'base' },
  { code: 'A0434', label: 'Specialty Care Transport (SCT)', payerCoverage: 'Medicare / Medicaid / Commercial',
    trigger: 'Interfacility with critical-care nurse/physician in attendance (rare for municipal)',
    category: 'base' },
  { code: 'A0429', label: 'BLS Emergency Transport', payerCoverage: 'Medicare / Medicaid / Commercial',
    trigger: 'Emergency dispatch + no ALS interventions documented',
    category: 'base' },
  { code: 'A0426', label: 'ALS Non-Emergency Transport, Level 1', payerCoverage: 'Medicare / Medicaid (CMN req) / Commercial',
    trigger: 'Non-emergency dispatch + ALS interventions + Certificate of Medical Necessity on file',
    category: 'base' },
  { code: 'A0428', label: 'BLS Non-Emergency Transport', payerCoverage: 'Medicare / Medicaid (CMN req) / Commercial',
    trigger: 'Non-emergency dispatch + BLS only + CMN on file',
    category: 'base' },
  { code: 'A0425', label: 'Ground Mileage (per statute mile)', payerCoverage: 'All payers — loaded miles only',
    trigger: 'Calculated from GPS breadcrumb at transport close',
    category: 'mileage' },
  { code: 'A0998', label: 'Ambulance Response and Treatment, No Transport', payerCoverage: 'Medicare (limited) / Medicaid (limited) / Commercial (varies)',
    trigger: 'Patient contact + treatment + refusal/treat-and-release. Medical necessity documentation required.',
    category: 'no_transport' },
  { code: 'A0382', label: 'BLS Disposable Supplies', payerCoverage: 'Commercial — varies',
    trigger: 'BLS transport with supply usage. Bundled for Medicare.',
    category: 'supply' },
  { code: 'A0394', label: 'ALS Disposable Supplies — IV', payerCoverage: 'Commercial — varies',
    trigger: 'ALS transport with IV established. Bundled for Medicare.',
    category: 'supply' },
  { code: 'A0422', label: 'Ambulance O2 / Life-Sustaining Equipment', payerCoverage: 'Commercial — varies',
    trigger: 'Oxygen administered during transport. Bundled for Medicare.',
    category: 'supply' },
];

// ============================================================
// Origin-Destination Modifiers (addendum § 4.2)
// ============================================================
export interface OriginDestinationModifier {
  char: string;
  label: string;
  autoSelect: string;
}

export const OD_MODIFIERS: OriginDestinationModifier[] = [
  { char: 'D', label: 'Diagnostic / therapeutic site',  autoSelect: 'Imaging center, dialysis, rehab' },
  { char: 'E', label: 'Residential / domiciliary / custodial', autoSelect: 'Residence, assisted living, group home' },
  { char: 'G', label: 'Hospital-based ESRD',            autoSelect: 'Hospital-based dialysis' },
  { char: 'H', label: 'Hospital',                        autoSelect: 'Acute care hospital' },
  { char: 'J', label: 'Non-hospital-based ESRD',        autoSelect: 'Freestanding dialysis' },
  { char: 'N', label: 'Skilled nursing facility',        autoSelect: 'SNF' },
  { char: 'P', label: 'Physician office',                autoSelect: "Doctor's office" },
  { char: 'R', label: 'Residence',                       autoSelect: 'Private residence (most common)' },
  { char: 'S', label: 'Scene of accident / acute event', autoSelect: 'MVC, workplace injury, public incident' },
  { char: 'X', label: 'Intermediate stop at physician office', autoSelect: 'Stop at office during transport' },
];

/** Invalid combinations per § 4.7 — HH blocked, etc. */
export const INVALID_MODIFIER_COMBOS = new Set(['HH']);

// ============================================================
// Level-of-Service derivation + validation (addendum § 4.5)
// ============================================================
export type LevelOfService = 'BLS' | 'ALS1' | 'ALS2' | 'SCT';

/** ALS interventions that elevate a call from BLS to ALS-1. */
const ALS_PROCEDURES = [
  /iv/i, /io\b/i, /endotracheal/i, /intub/i, /supraglottic|igel|i-gel|king|lma/i,
  /12[- ]?lead/i, /cardiac monitor/i, /capnograph/i, /cpap/i, /bipap/i,
  /transcutan/i, /cardiovers/i, /defib/i, /pacing/i, /chest decompress|needle decompress/i,
  /cric/i, /central/i,
];

const ALS_MED_PATTERNS = [
  /epinephrine|epi\b/i, /atropine/i, /amiodarone/i, /adenosine/i, /lidocaine/i,
  /mag sul|magnesium/i, /dopamine/i, /norepin/i, /nitroglycerin|ntg\b/i,
  /morphine|fentanyl|ketamine|dilaudid|hydromorphone/i, /midazolam|versed/i,
  /naloxone|narcan/i, /dextrose|d10|d50/i, /calcium chlor/i, /sodium bicarb/i,
  /albuterol|levalbuterol|duoneb|ipratropium/i,
];

/** ALS-2 trigger interventions (any one of these qualifies). */
const ALS2_TRIGGERS = [
  /manual defib/i, /transcutan/i, /cardiovers/i,
  /chest decompress|needle decompress/i, /endotracheal|intubat|ett\b/i,
];

interface EpcrForBilling {
  epcr: EPCR;
  procedures: Procedure[];
  medications: MedicationGiven[];
}

export function deriveLevelOfService({ procedures, medications }: EpcrForBilling): LevelOfService {
  const procNames = procedures.map((p) => `${p.procedure_type} ${p.body_site ?? ''}`).join(' | ');
  const medNames = medications.map((m) => `${m.drug_name} ${m.patient_response ?? ''}`).join(' | ');
  const alsMedsGiven = medications.filter((m) =>
    ALS_MED_PATTERNS.some((re) => re.test(m.drug_name)),
  ).length;

  // ALS-2 triggers
  if (ALS2_TRIGGERS.some((re) => re.test(procNames) || re.test(medNames))) return 'ALS2';
  if (alsMedsGiven >= 3) return 'ALS2';

  // ALS-1 interventions
  if (ALS_PROCEDURES.some((re) => re.test(procNames))) return 'ALS1';
  if (alsMedsGiven >= 1) return 'ALS1';

  return 'BLS';
}

/** Map a LOS + emergency flag to an HCPCS base code. */
export function hcpcsForLos(los: LevelOfService, emergency: boolean): string {
  if (los === 'SCT')  return 'A0434';
  if (los === 'ALS2') return 'A0433';
  if (los === 'ALS1') return emergency ? 'A0427' : 'A0426';
  return emergency ? 'A0429' : 'A0428';
}

// ============================================================
// Denial-prevention checks (addendum § 4.7)
// ============================================================
export interface DenialCheck {
  key: string;
  label: string;
  ok: boolean;
  detail?: string;
  severity: 'blocker' | 'warning';
}

interface DenialInput extends EpcrForBilling {
  emergency: boolean;
  modifierOrigin: string | null;
  modifierDestination: string | null;
  mileageConfirmed: boolean;
  cmnOnFile: boolean;
  patientSignatureOnFile: boolean;
  declaredLos?: LevelOfService;
}

export function runDenialChecks(input: DenialInput): DenialCheck[] {
  const out: DenialCheck[] = [];
  const derived = deriveLevelOfService(input);

  out.push({
    key: 'patient_sig',
    label: 'Patient (or representative) signature on file',
    ok: input.patientSignatureOnFile,
    severity: 'blocker',
    detail: 'Missing signatures cause automatic denial by CMS. Capture on the Signatures tab before export.',
  });

  out.push({
    key: 'mileage_confirmed',
    label: 'Loaded mileage confirmed',
    ok: input.mileageConfirmed,
    severity: 'blocker',
    detail: 'A0425 mileage must be confirmed before claim generation.',
  });

  if (input.modifierOrigin && input.modifierDestination) {
    const combo = `${input.modifierOrigin}${input.modifierDestination}`;
    out.push({
      key: 'od_modifier',
      label: 'Origin-destination modifier valid',
      ok: !INVALID_MODIFIER_COMBOS.has(combo),
      severity: 'blocker',
      detail: `Modifier combo "${combo}" is not allowed by CMS.`,
    });
  } else {
    out.push({
      key: 'od_modifier_present',
      label: 'Origin-destination modifier present',
      ok: false,
      severity: 'blocker',
      detail: 'Two-character origin+destination modifier required on every ambulance claim.',
    });
  }

  if (!input.emergency) {
    out.push({
      key: 'cmn',
      label: 'Certificate of Medical Necessity on file (non-emergency)',
      ok: input.cmnOnFile,
      severity: 'blocker',
      detail: 'Non-emergency transports (A0426 / A0428) require a current CMN.',
    });
  }

  if (input.declaredLos) {
    if (input.declaredLos === 'ALS1' && derived === 'BLS') {
      out.push({
        key: 'los_downgrade',
        label: 'Level-of-service mismatch',
        ok: false,
        severity: 'warning',
        detail: 'Declared ALS-1 but no ALS interventions documented. Review or add procedure/medication before export.',
      });
    } else if (input.declaredLos === 'BLS' && (derived === 'ALS1' || derived === 'ALS2')) {
      out.push({
        key: 'los_upgrade',
        label: 'Level-of-service upgrade available',
        ok: false,
        severity: 'warning',
        detail: `Documented care supports ${derived}. You can keep BLS or upgrade the claim.`,
      });
    }
  }

  return out;
}

// ============================================================
// Build the submittable billing bundle
// ============================================================
export interface BillingBundle {
  epcrId: string;
  baseHcpcs: string;
  mileageHcpcs: 'A0425' | null;
  loadedMiles: number | null;
  originModifier: string | null;
  destinationModifier: string | null;
  combinedModifier: string | null;
  levelOfService: LevelOfService;
  declaredLos?: LevelOfService;
  emergency: boolean;
  denialChecks: DenialCheck[];
  blockerCount: number;
  warningCount: number;
}

export function buildBillingBundle(args: DenialInput): BillingBundle {
  const los = args.declaredLos ?? deriveLevelOfService(args);
  const baseHcpcs = hcpcsForLos(los, args.emergency);
  const checks = runDenialChecks({ ...args, declaredLos: los });
  const combined = args.modifierOrigin && args.modifierDestination
    ? `${args.modifierOrigin}${args.modifierDestination}` : null;

  return {
    epcrId: args.epcr.id,
    baseHcpcs,
    mileageHcpcs: args.mileageConfirmed ? 'A0425' : null,
    loadedMiles: args.epcr.loaded_mileage_miles,
    originModifier: args.modifierOrigin,
    destinationModifier: args.modifierDestination,
    combinedModifier: combined,
    levelOfService: los,
    declaredLos: args.declaredLos,
    emergency: args.emergency,
    denialChecks: checks,
    blockerCount: checks.filter((c) => c.severity === 'blocker' && !c.ok).length,
    warningCount: checks.filter((c) => c.severity === 'warning' && !c.ok).length,
  };
}
